import { Box, Button, Spacer, Text } from '@chakra-ui/react';
import { getAssetUrl } from '@cronos-app/chakra';
import { CdcCardType } from '@ui/views/card-cronos/core/types';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { CdcCard } from '../shared/CdcCard';
import { CollapseWrapper } from '../shared/CollapseWrapper';
import { ProgressBar } from './ProgressBar';
import type { StepItem } from './StepList';
import { StepItemContainer, StepList } from './StepList';
import { useToggleReminderStore } from './ToggleReminder';
import { useCardAction } from './useCardAction';

enum Step {
  Unbind = 1,
  Tips = 2,
}

export const UnbindView = () => {
  const [step, setStep] = useState(Step.Unbind);
  return (
    <>
      <ProgressBar
        total={2}
        done={step}
        mb="6"
        px={{
          base: 5,
          desktop: 6,
        }}
      />
      {step === Step.Unbind ? <ActionView setStep={setStep} /> : <TipsView />}
    </>
  );
};

const ActionView = ({ setStep }: { setStep: (step: Step) => void }) => {
  const [loading, setLoading] = useState(false);
  const t = useTranslations();
  const { unbindCard } = useCardAction();
  return (
    <>
      <Box
        px={{
          base: 5,
          desktop: 6,
        }}
      >
        <Text textStyle="subtitle1" textAlign="center" mb="48px">
          1. {t('card.Unlink wallet')}
        </Text>
        <Text textStyle="body2" textAlign="center" mb="16px">
          {t(
            'card.Unlinking your wallet will disconnect it from your Crypto-com and DeFi wallets',
          )}
        </Text>
        <CdcCard type={CdcCardType.None} />
      </Box>
      <Spacer minH="26" />
      <Box
        borderTop="1px solid"
        borderColor="brand.gray-scale-border-10"
        px="5"
        py="4"
      >
        <Button
          w="full"
          variant="ghost"
          size="sm"
          bgColor="#159DFF"
          rounded="4px"
          onClick={() => {
            setLoading(true);
            unbindCard()
              .then(() => {
                setStep(Step.Tips);
              })
              .catch(() => {})
              .finally(() => {
                setLoading(false);
              });
          }}
          isLoading={loading}
          textTransform="unset"
        >
          {t('card.Unlink')}
        </Button>
      </Box>
    </>
  );
};

const TipsView = () => {
  const t = useTranslations();
  return (
    <>
      <Box
        px={{
          base: 5,
          desktop: 6,
        }}
      >
        <Text textStyle="subtitle1" textAlign="center">
          2. {t('card.Disable auto top-up')}
        </Text>
        <Text textStyle="body2" textAlign="center">
          {t(
            'card.After unlinking your wallet, open the Crypto-com app and disable your auto top-up',
          )}
        </Text>
        <StepItemContainer
          title={t('card.Disable auto top-up')}
          image={getAssetUrl(
            '/images/cdc-bind-card/cdc-disable-auto-1-new.png',
          )}
          my="6"
        />
        <CollapseWrapper title={t('card.How do I toggle it off?')}>
          <StepList steps={AUTO_STEPS} mt="1" />
        </CollapseWrapper>
      </Box>
      <Spacer minH="6" />
      <Box
        borderTop="1px solid"
        borderColor="brand.gray-scale-border-10"
        px="5"
        pb="4"
        pt="2"
      >
        <Button
          variant="action"
          color="#159DFF"
          w="full"
          mb="2"
          onClick={useToggleReminderStore.getState().open}
          textTransform="unset"
        >
          {t('card.Do it later')}
        </Button>
        {/* <Link href={URL_SCHEME.cdcAppAutoTopUp} isExternal>
          <Button
            variant="ghost"
            bgColor="#159DFF"
            rounded="4px"
            size="sm"
            w="full"
            textTransform="unset"
          >
            {t('card.Open the Crypto-com app')}
          </Button>
        </Link> */}
      </Box>
    </>
  );
};

const AUTO_STEPS: StepItem[] = [
  {
    title: (t) => t('card.Open the Crypto-com app and tap on ‘Card’'),
    image: getAssetUrl('/images/cdc-bind-card/cdc-card-entry-1.png'),
  },
  {
    title: (t) => t('card.Toggle off ‘Auto Top-Up With Cronos Chain’'),
    image: getAssetUrl('/images/cdc-bind-card/auto-on-setting-1.png'),
  },
  {
    title: (t) => t('card.Click ‘Confirm’'),
    image: getAssetUrl('/images/cdc-bind-card/auto-off-confirm-new.png'),
  },
  {
    title: (t) => t('card.Enter passcode'),
    image: getAssetUrl('/images/cdc-bind-card/auto-off-pwd-new.png'),
  },
  {
    title: (t) => t('card.Auto top-up disabled'),
    image: getAssetUrl('/images/cdc-bind-card/auto-off-setting-1.png'),
  },
] as const;
